// Tells the user, before they start typing budgets, whether a pricing strategy actually
// covers what they are about to enter - and gives them a way out of every dead end.
//
// What this replaces: a bare paragraph saying "A pricing formula was not found ...
// please adjust your selection or contact DG-KnowRisk@StoneX.com", shown for BOTH "no
// strategy exists" and "several strategies match", with the Start button silently
// disabled. There was no way to tell those two apart, no way to see which strategies
// nearly matched, and nothing the user could do about either without leaving the page.
//
// Every state below therefore carries an action: create one, pick one, add the missing
// components, or continue without a strategy at all.
import { Alert, AlertTitle, Box, Button, Chip } from "@mui/material";
import {
  BudgetPricingStrategyModel,
  GetCustomerDataResponse,
} from "../../../../../Generated/Commodity-Management-Api";
import { StoneXSelect } from "../../../../StoneXMui";

/** What the current commodity/location/counterparty selection resolves to. */
export type PricingStrategyState =
  /** Not enough of the entry chosen yet to look anything up. */
  | { kind: "pending" }
  /** Exactly one strategy covers the entry and it has price components. */
  | { kind: "ready"; strategy: BudgetPricingStrategyModel }
  /** Several cover it - the user has to say which. */
  | { kind: "choose"; matches: BudgetPricingStrategyModel[] }
  /** One covers it but prices nothing, so budgets would save with no price. */
  | { kind: "noComponents"; strategy: BudgetPricingStrategyModel }
  /** Nothing covers it. */
  | { kind: "missing" };

export interface PricingStrategyNoticeProps {
  state: PricingStrategyState;
  customerData: GetCustomerDataResponse;
  /** How the entry is scoped, for the "nothing covers Corn / Plant 3" message. */
  entryDescription: string;
  /** The user chose to enter budgets with no strategy at all. */
  isSkipping: boolean;
  /** Hide the actions while the grid is locked in or a save is running. */
  disabled?: boolean;
  onSelectStrategy: (strategy: BudgetPricingStrategyModel | null) => void;
  onCreateStrategy: () => void;
  onEditStrategy: (strategy: BudgetPricingStrategyModel) => void;
  onSkip: () => void;
  onCancelSkip: () => void;
}

/** "Corn · All locations · Cargill" - the scope a strategy applies to. */
export function describeStrategyScope(
  strategy: BudgetPricingStrategyModel,
  customerData: GetCustomerDataResponse,
): string {
  const commodity = strategy.commodityId === null || strategy.commodityId === undefined
    ? "All commodities"
    : (customerData.commodities ?? []).find((x) => x.commodityId === strategy.commodityId)?.commodityName
      ?? `Commodity ${strategy.commodityId}`;

  const location = strategy.locationId === null || strategy.locationId === undefined
    ? "All locations"
    : (customerData.locations ?? []).find((x) => x.locationId === strategy.locationId)?.locationName
      ?? `Location ${strategy.locationId}`;

  const counterparty = strategy.counterpartyId === null || strategy.counterpartyId === undefined
    ? "All counterparties"
    : (customerData.counterparties ?? []).find((x) => x.counterpartyId === strategy.counterpartyId)?.counterpartyName
      ?? `Counterparty ${strategy.counterpartyId}`;

  return [commodity, location, counterparty].join(" · ");
}

function ComponentChips(props: { strategy: BudgetPricingStrategyModel }) {
  const { strategy } = props;

  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
      {(strategy.components ?? []).map((component, index) => (
        <Chip
          key={`${component.budgetPricingComponentId}-${index}`}
          size="small"
          variant="outlined"
          label={component.marketPriceProvider
            ? `${component.componentType} – ${component.marketPriceProvider}`
            : component.componentType}
        />
      ))}
    </Box>
  );
}

export default function PricingStrategyNotice(props: PricingStrategyNoticeProps) {
  const {
    state,
    customerData,
    entryDescription,
    isSkipping,
    disabled,
    onSelectStrategy,
    onCreateStrategy,
    onEditStrategy,
    onSkip,
    onCancelSkip,
  } = props;

  if (isSkipping) {
    return (
      <Alert
        severity="info"
        variant="outlined"
        action={!disabled && (
          <Button size="small" onClick={onCancelSkip}>Use a strategy</Button>
        )}
      >
        <AlertTitle>Adding budgets without a pricing strategy</AlertTitle>
        The grid will have a volume column and no price columns. Prices can be added later by
        creating a strategy and re-entering these budgets.
      </Alert>
    );
  }

  if (state.kind === "pending") {
    return (
      <Alert severity="info" variant="outlined">
        Choose a commodity to see which pricing strategy applies.
      </Alert>
    );
  }

  if (state.kind === "ready") {
    return (
      <Alert
        severity="success"
        variant="outlined"
        action={!disabled && (
          <Button size="small" onClick={() => onEditStrategy(state.strategy)}>Edit</Button>
        )}
      >
        <AlertTitle>Pricing strategy: {state.strategy.name}</AlertTitle>
        {describeStrategyScope(state.strategy, customerData)}
        <ComponentChips strategy={state.strategy} />
      </Alert>
    );
  }

  if (state.kind === "choose") {
    return (
      <Alert severity="warning" variant="outlined">
        <AlertTitle>{state.matches.length} pricing strategies cover this entry</AlertTitle>
        Pick the one these budgets should be priced against. The most specific is listed first.
        <Box sx={{ mt: 1, maxWidth: 520 }}>
          <StoneXSelect
            label="Pricing Strategy"
            options={state.matches}
            getId={(x) => x.budgetPricingStrategyId}
            getOptionLabel={(x) => `${x.name} (${describeStrategyScope(x, customerData)})`}
            value={null}
            onChange={(value) => onSelectStrategy(value)}
            disabled={disabled}
            fullWidth
          />
        </Box>
      </Alert>
    );
  }

  if (state.kind === "noComponents") {
    return (
      <Alert
        severity="warning"
        variant="outlined"
        action={!disabled && (
          <Button size="small" onClick={() => onEditStrategy(state.strategy)}>Add components</Button>
        )}
      >
        <AlertTitle>{state.strategy.name} has no price components</AlertTitle>
        It covers {describeStrategyScope(state.strategy, customerData)}, but defines no price
        components - budgets entered against it would have a volume and no price. Add at least one
        component to it, or continue without a pricing strategy.
        <Box sx={{ mt: 1 }}>
          <Button size="small" onClick={onSkip} disabled={disabled}>
            Continue without a pricing strategy
          </Button>
        </Box>
      </Alert>
    );
  }

  return (
    <Alert
      severity="warning"
      variant="outlined"
      action={!disabled && (
        <Button size="small" variant="outlined" onClick={onCreateStrategy}>
          Create pricing strategy
        </Button>
      )}
    >
      <AlertTitle>No pricing strategy covers this entry</AlertTitle>
      Nothing is set up for <strong>{entryDescription}</strong>. A pricing strategy defines the
      price components each budget row is entered against, so one has to exist before these
      budgets can be priced.
      <Box sx={{ mt: 1 }}>
        <Button size="small" onClick={onSkip} disabled={disabled}>
          Continue without a pricing strategy
        </Button>
      </Box>
    </Alert>
  );
}
